import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Quote, QuoteFilters, QuoteMetrics } from '../../types/quote.types';
import { QuoteFilterType, QuoteFilterStatus } from '../../types/quote.enums';

interface QuotesState {
  quotes: Quote[];
  metrics: QuoteMetrics;
  loading: boolean;
  error: string | null;
  filters: QuoteFilters;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
  selectedQuoteIds: string[];
  sortConfig: {
    key: string;
    direction: 'asc' | 'desc' | null;
  };
}

const initialState: QuotesState = {
  quotes: [],
  metrics: {
    totalQuotes: 0,
    draftQuotes: 0,
    sentQuotes: 0,
    viewedQuotes: 0,
    acceptedQuotes: 0,
    rejectedQuotes: 0,
    expiredQuotes: 0,
    convertedQuotes: 0,
    pendingQuotes: 0,
    totalValue: 0,
    acceptedValue: 0,
    acceptanceRate: 0,
    newThisMonth: 0,
  },
  loading: false,
  error: null,
  filters: {
    search: '',
    type: QuoteFilterType.ALL,
    status: QuoteFilterStatus.ALL,
    clientId: '',
    dateRange: { from: null, to: null },
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  },
  selectedQuoteIds: [],
  sortConfig: {
    key: '',
    direction: null,
  },
};

const quotesSlice = createSlice({
  name: 'quotes',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Data management
    setQuotes: (state, action: PayloadAction<Quote[]>) => {
      state.quotes = action.payload;
    },

    setMetrics: (state, action: PayloadAction<QuoteMetrics>) => {
      state.metrics = action.payload;
    },

    // Filters
    setFilters: (state, action: PayloadAction<Partial<QuoteFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
    },

    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },

    // Pagination
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },

    setPagination: (state, action: PayloadAction<{
      totalItems: number;
      totalPages: number;
    }>) => {
      state.pagination.totalItems = action.payload.totalItems;
      state.pagination.totalPages = action.payload.totalPages;
    },

    // Sorting
    setSortConfig: (state, action: PayloadAction<{
      key: string;
      direction: 'asc' | 'desc' | null;
    }>) => {
      state.sortConfig = action.payload;
    },

    // Selection
    setSelectedQuoteIds: (state, action: PayloadAction<string[]>) => {
      state.selectedQuoteIds = action.payload;
    },

    toggleQuoteSelection: (state, action: PayloadAction<string>) => {
      const quoteId = action.payload;
      const index = state.selectedQuoteIds.indexOf(quoteId);
      if (index > -1) {
        state.selectedQuoteIds.splice(index, 1);
      } else {
        state.selectedQuoteIds.push(quoteId);
      }
    },

    clearSelection: (state) => {
      state.selectedQuoteIds = [];
    },

    // Quote operations
    updateQuote: (state, action: PayloadAction<Quote>) => {
      const index = state.quotes.findIndex(quote => quote.id === action.payload.id);
      if (index !== -1) {
        state.quotes[index] = action.payload;
      }
    },

    removeQuote: (state, action: PayloadAction<string>) => {
      state.quotes = state.quotes.filter(quote => quote.id !== action.payload);
      state.selectedQuoteIds = state.selectedQuoteIds.filter(id => id !== action.payload);
    },

    addQuote: (state, action: PayloadAction<Quote>) => {
      state.quotes.unshift(action.payload);
    },
  },
});

export const {
  setLoading,
  setError,
  setQuotes,
  setMetrics,
  setFilters,
  clearFilters,
  setCurrentPage,
  setPageSize,
  setPagination,
  setSortConfig,
  setSelectedQuoteIds,
  toggleQuoteSelection,
  clearSelection,
  updateQuote,
  removeQuote,
  addQuote,
} = quotesSlice.actions;

export default quotesSlice.reducer;



